import React, { useState, useContext, useEffect } from 'react';
import {
	Box,
	Button,
	Flex,
	FormControl,
	FormErrorMessage,
	Text,
} from 'src/components/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { StringInputHook } from 'src/components/common/form';
import { validateWebsiteUrl } from 'src/lib/utils';
import ScrapedInfoForm from './ScrapedInfoForm';
import { IScrapeDataOutput } from 'src/lib/schemas/scrapping';
import {
	IGetDomainDataSchema,
	GetDomainDataSchema,
} from 'src/lib/schemas/scrap/getDomainData';
import { getFullUrlData } from 'src/services/scraping';
import ThreeDotsAnimation from 'src/components/assistantChat/animation/ThreeDotsAnimation';
import useRoute from 'src/hooks/useRoute';
import { IAppInputLoading } from 'src/contexts/app/AppInputConfigContext';
import { DomainContext } from 'src/contexts/DomainContext';
import { CampaignContext } from 'src/contexts';
import UserContext from 'src/contexts/user/UserContext';

interface IDomainNotFound {
	prevDomain?: string;
	onDomainSubmit?: (newDomain: string) => void;
	onDataSubmit?: any;
	withManualOption?: boolean;
	onDataLoaded?: (data: IScrapeDataOutput) => void;
	shouldShowForm?: boolean;
	setIsDataLoading?: React.Dispatch<React.SetStateAction<boolean>>;
	isDataLoading?: boolean;
	setIsExtraDataLoading?: (loadingInputs: IAppInputLoading) => void;
	onStartScrapping?: () => void;
	buttonText?: string;
	isCampaign?: boolean;
	onScrapingError?: () => void;
	disableFormWrapper?: boolean; // New prop to control form wrapper
}

const DomainForm = ({
	prevDomain,
	withManualOption,
	onDomainSubmit,
	onDataSubmit,
	onDataLoaded,
	shouldShowForm = false,
	setIsDataLoading,
	isDataLoading,
	setIsExtraDataLoading,
	onStartScrapping,
	onScrapingError,
	buttonText = 'Fill it in',
	isCampaign = false,
	disableFormWrapper = false, // Default to false for backward compatibility
}: IDomainNotFound) => {
	const [showForm, setShowForm] = useState(false);
	const [domainData, setDomainData] = useState<IScrapeDataOutput>();
	const [domainNotFound, setDomainNotFound] = useState(false);
	const [isManuallyChanged, setIsManuallyChanged] = useState(false);
	const { removeUrlPrefix } = useRoute();
	const formMethods = useForm<IGetDomainDataSchema>({
		resolver: zodResolver(GetDomainDataSchema),
	});
	const { setValue, setError, clearErrors, watch, setFocus, formState, reset } =
		formMethods;
	const url = watch('url');
	const { id: campaignId } = useContext(CampaignContext);
	const { domainUrl, setDomainUrl } = useContext(DomainContext);
	const { account } = useContext(UserContext);

	useEffect(() => {
		if (isCampaign && !isManuallyChanged) {
			const urlToSet = domainUrl || prevDomain;
			if (urlToSet) {
				setValue('url', urlToSet, { shouldDirty: false });
				setDomainUrl('');
			}
		}
	}, [
		isCampaign,
		domainUrl,
		prevDomain,
		setValue,
		setDomainUrl,
		isManuallyChanged,
	]);

	const handleSubmit = async () => {
		if (url.trim() === '') {
			setError('url', { message: 'Website URL is required' });
			return;
		}

		const { isValid, updatedUrl } = validateWebsiteUrl(url);
		if (!isValid) {
			setError('url', { message: 'Please enter a valid URL' });
			return;
		}

		if (setIsDataLoading) {
			setIsDataLoading(true);
		}
		onStartScrapping && onStartScrapping();
		shouldShowForm && setShowForm(true);
		setDomainData(undefined);
		const validatedUrl = updatedUrl;

		const finalUrl = validatedUrl;
		setValue('url', finalUrl, { shouldDirty: false });
		onDomainSubmit && onDomainSubmit(finalUrl);
		setDomainUrl(finalUrl);

		try {
			const data = await getFullUrlData(decodeURIComponent(validatedUrl));
			onDataLoaded && onDataLoaded(data);
			setDomainData(data);
			setDomainNotFound(false);
			setIsManuallyChanged(false);
		} catch (error) {
			console.error('Scraping failed:', error);
			setDomainNotFound(true);
			onScrapingError && onScrapingError();
		} finally {
			if (setIsDataLoading) {
				setIsDataLoading(false);
			}
		}
	};
	useEffect(() => {
		if (campaignId === 'new') {
			reset({ url: '' });
		}
	}, [campaignId, reset]);
	const handleWebsiteLinkChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		updateValue = false,
	) => {
		const inputUrl = e.target.value;
		if (inputUrl.trim() === '') {
			clearErrors('url');
			return;
		}
		const { isValid, updatedUrl } = validateWebsiteUrl(inputUrl);
		if (!isValid) {
			setError('url', { message: 'Please enter a valid URL' });
			return;
		}
		if (updateValue) {
			setValue('url', updatedUrl);
		}
		clearErrors('url');
		setIsManuallyChanged(true);
	};

	return (
		<FormProvider {...formMethods}>
			{(() => {
				const formContent = (
					<>
						<Flex
							justifyContent={{ base: 'center', md: 'flex-start' }}
							flexDirection={{ base: 'column', md: 'row' }}
							gap={2}
							alignItems={{ base: 'stretch', md: 'flex-end' }}
						>
							<StringInputHook
								name="url"
								label="Url"
								inputProps={{
									w: { base: '100%', md: '300px', lg: '400px' },
									onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
										handleWebsiteLinkChange(e, true),
									onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
										handleWebsiteLinkChange(e),
									onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											handleSubmit();
										}
									},
								}}
								placeholder="http://"
								formControlProps={{ w: { base: '100%', md: 'auto' } }}
								required
								withErrorMessage={false}
							/>
							<Button
								variant="orangeSolid"
								w={{ base: '100%', md: '130px', lg: '150px' }}
								mt={{ base: 2, md: 0 }}
								onClick={handleSubmit}
								isLoading={isDataLoading}
							>
								{buttonText}
							</Button>

							{withManualOption && !showForm && (
								<Button
									variant="blankOutline"
									w={{ base: '100%', md: '130px', lg: '150px' }}
									mt={{ base: 2, md: 0 }}
									onClick={() => {
										setShowForm(true);
										setFocus('url');
									}}
								>
									Enter Manually
								</Button>
							)}
							{isDataLoading && (
								<Flex pb="8px">
									<ThreeDotsAnimation />
								</Flex>
							)}
							{domainNotFound && !isDataLoading && (
								<Text color="red">
									Unable to obtain business information. Please enter it
									manually or try a new website.
								</Text>
							)}
						</Flex>
						{account?.websiteLink && url !== account.websiteLink && (
							<Text
								as="span"
								fontSize="sm"
								color="orange.500"
								mt={3}
								px={2}
								py={1}
								borderWidth="1px"
								borderColor="orange.300"
								borderRadius="md"
								cursor="pointer"
								display="inline-block"
								_hover={{
									bg: 'orange.50',
									borderColor: 'orange.500',
								}}
								onClick={() => {
									setValue('url', account.websiteLink!);
									clearErrors('url');
									setIsManuallyChanged(true);
								}}
							>
								{account.websiteLink}
							</Text>
						)}
						<FormControl isInvalid={Boolean(formState.errors['url'])}>
							<FormErrorMessage>
								{formState.errors['url']?.message}
							</FormErrorMessage>
						</FormControl>
						<Box mt={5}>
							{showForm && (
								<ScrapedInfoForm
									isDataLoading={isDataLoading}
									data={domainData}
									onDataSubmit={onDataSubmit}
									websiteLink={url}
									errors={formState.errors}
									isManualEntry={!domainData}
								/>
							)}
						</Box>
					</>
				);

				return disableFormWrapper ? formContent : <form>{formContent}</form>;
			})()}
		</FormProvider>
	);
};

export default DomainForm;
